"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useLocalDateChange } from "@/hooks/use-local-date-change";
import { useRefetchWhenStale } from "@/hooks/use-refetch-when-stale";
import { useStoreLocation } from "@/context/store-location-context";
import {
  getDeliveryWindows,
  getDeliveryDaysForAddress,
  STALE_AFTER_MS,
  type DeliveryDay,
} from "@/components/checkout/delivery-window-chooser";

type ExpressDeliveryInfo = {
  is_express_delivery_option_hidden_from_user: boolean;
  is_express_delivery_crossed_out: boolean;
  express_delivery_time_estimate_text: string;
};

type DeliveryWindowsResponse = {
  shipping_info_id_to_delivery_windows: Record<string, DeliveryDay[]>;
  shipping_info_id_to_express_delivery_info: Record<string, ExpressDeliveryInfo>;
  default_slots: DeliveryDay[];
};

function getFirstAvailableSlot(days: DeliveryDay[], expressInfo: ExpressDeliveryInfo | null): string | null {
  // Prefer express if available for today
  if (
    expressInfo &&
    !expressInfo.is_express_delivery_option_hidden_from_user &&
    !expressInfo.is_express_delivery_crossed_out &&
    !!expressInfo.express_delivery_time_estimate_text
  ) {
    return expressInfo.express_delivery_time_estimate_text;
  }

  for (const day of days) {
    const slot = day.timeSlots.find((s) => s.is_available && !s.is_sold_out && !s.is_option_hidden);
    if (slot) return `${day.dayString}, ${slot.bucket}`;
  }
  return null;
}

export function useFirstDeliverySlot(addressId: string | number | null) {
  const [slot, setSlot] = useState<string | null>(null);
  const lastFetchedAtRef = useRef(0);
  const latestRequestIdRef = useRef(0);
  const { storeLocation } = useStoreLocation();
  const city = storeLocation.city;

  const fetch = useCallback(async () => {
    // Switching address quickly (or a refocus refetch overlapping one) can
    // resolve out of order — only the latest request may set the slot, or the
    // header could show the previous address's next delivery.
    const requestId = ++latestRequestIdRef.current;
    const result = await getDeliveryWindows<DeliveryWindowsResponse>(city);
    if (requestId !== latestRequestIdRef.current) return;
    if (!result.ok || !result.data) return;
    lastFetchedAtRef.current = Date.now();

    const addressIdStr = addressId ? String(addressId) : null;
    const days = getDeliveryDaysForAddress(result.data, addressId);

    const expressInfo = addressIdStr
      ? result.data.shipping_info_id_to_express_delivery_info?.[addressIdStr] ?? null
      : null;

    setSlot(getFirstAvailableSlot(days, expressInfo));
  }, [addressId, city]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // The slot text embeds the server's dayString ("Hoy"), which goes stale once
  // the user's calendar day rolls over — re-fetch so the label stays accurate.
  useLocalDateChange(fetch);

  // Within the same day the first slot passes or sells out too (e.g. still
  // showing "Hoy, 8am - 10am" at noon) — refresh once the data is stale.
  useRefetchWhenStale(fetch, lastFetchedAtRef, STALE_AFTER_MS);

  return slot;
}
